import React, { useEffect, useRef, useState } from "react";
import { RadioGroup } from "@truevault/aquilo";
import clsx from "clsx";
import { OrganizationDataRecipientDto } from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { UpdateVendorArgs } from "../components/workflows/create-data-recipient/ClassificationForDataRecipientPage";
import { VENDOR_CATEGORY_AD_NETWORK } from "../../../types/Vendor";
import { ManagedByVendorSection } from "./ManagedByVendorSection";
import type { VendorInventoryItem } from "./vendorInventoryUtils";

export interface VendorTargetedAdvertisingSectionProps {
  /**
   * Prefix for RadioGroup `name` (and derived input ids). Must be unique per mounted instance so
   * radios in the inventory slideout and vendor details modal do not share one HTML radio group.
   */
  radioGroupNamePrefix: string;
  vendor: VendorInventoryItem;
  org: OrganizationDto;
  updateVendor: (v: UpdateVendorArgs) => Promise<void>;
  /** Full vendor list (e.g. for “managed by another vendor” ad-network UI). */
  allVendors: VendorInventoryItem[];
  portalAutocompleteDropdown?: boolean;
  onManagerAdded?: () => void;
  className?: string;
}

type TargetedAdvertisingAnswers = Partial<
  Pick<OrganizationDataRecipientDto, "ccpaIsSharing" | "usesCustomAudience">
>;

export function VendorTargetedAdvertisingSection({
  radioGroupNamePrefix,
  vendor,
  org,
  updateVendor,
  allVendors,
  portalAutocompleteDropdown = false,
  onManagerAdded,
  className,
}: VendorTargetedAdvertisingSectionProps) {
  // Answers show the moment they're picked and save in the background: a vendor save regenerates the
  // privacy notice's GDPR/PIPEDA sections server-side, which took seconds behind a full-modal spinner.
  // `pending` holds picks the vendor props don't reflect yet; saves run one at a time, each sending
  // every answer given so far, so an earlier save can never land after (and undo) a later one.
  const [pending, setPending] = useState<TargetedAdvertisingAnswers>({});
  const answersRef = useRef<TargetedAdvertisingAnswers>({});
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const recipientRef = useRef(vendor.recipientData);
  recipientRef.current = vendor.recipientData;

  const savedSharing = vendor.recipientData?.ccpaIsSharing;
  const savedCustomAudience = vendor.recipientData?.usesCustomAudience;
  useEffect(() => {
    setPending((prev) => {
      const next = { ...prev };
      if (next.ccpaIsSharing !== undefined && next.ccpaIsSharing === savedSharing) {
        delete next.ccpaIsSharing;
      }
      if (
        next.usesCustomAudience !== undefined &&
        next.usesCustomAudience === savedCustomAudience
      ) {
        delete next.usesCustomAudience;
      }
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [savedSharing, savedCustomAudience]);

  if (!vendor.recipientData) {
    return null;
  }
  const recipient = { ...vendor.recipientData, ...pending };

  const saveAnswers = (answers: TargetedAdvertisingAnswers) => {
    answersRef.current = { ...answersRef.current, ...answers };
    setPending((prev) => ({ ...prev, ...answers }));
    const save = async () => {
      try {
        await updateVendor({
          vendorId: vendor.vendorId,
          vendorSettings: { ...recipientRef.current!, ...answersRef.current },
        });
      } catch {
        // Fall back to what's saved so the radios don't claim an answer that didn't stick. The
        // host's updateVendor already showed the error.
        // A key picked again since this save was queued belongs to the later save; leave it.
        const failedKeys = (Object.keys(answers) as (keyof TargetedAdvertisingAnswers)[]).filter(
          (key) => answersRef.current[key] === answers[key],
        );
        failedKeys.forEach((key) => delete answersRef.current[key]);
        setPending((prev) => {
          const next = { ...prev };
          failedKeys.forEach((key) => delete next[key]);
          return next;
        });
      }
    };
    saveQueueRef.current = saveQueueRef.current.then(save);
  };

  return (
    <div className={clsx(className)}>
      <div className="flex justify-between items-start gap-[12px]">
        <div>
          <div className="font-medium text-gray-700">
            {org.name} is sharing personal information with {vendor.name}
          </div>
          <div className="text-neutral-500">
            Select "yes" if you may be using this vendor for advertising, marketing, analytics, or
            data sharing purposes.
          </div>
        </div>
        <RadioGroup
          name={`${radioGroupNamePrefix}-isSharing`}
          direction="horizontal"
          options={[
            { label: "Yes", value: "YES" },
            { label: "No", value: "NO" },
          ]}
          selectedValue={
            recipient.ccpaIsSharing === null ? undefined : recipient.ccpaIsSharing ? "YES" : "NO"
          }
          onChange={(newValue: string) => saveAnswers({ ccpaIsSharing: newValue === "YES" })}
        />
      </div>

      {recipient.ccpaIsSharing && (
        <div className="flex justify-between items-start gap-[12px] mt-[12px]">
          <div className="pt-[6px]">
            <svg
              width="12"
              height="13"
              viewBox="0 0 12 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.25 8.26172L6.75 12.7617L5.69531 11.707L8.40234 9H0V0H1.51172V7.52344H8.40234L5.69531 4.81641L6.75 3.76172L11.25 8.26172Z"
                fill="black"
              />
            </svg>
          </div>
          <div>
            <div className="text-gray-700 font-medium">
              {org.name} uses the {'"'}Custom Audience{'"'} feature for {vendor.name}
            </div>
            <div className="text-neutral-500">
              Custom Audience is a service offered by most ad networks in which a business can
              upload a list of their customers or contacts, and the ad network will either deliver
              ads directly to those people and/or use it to create a {'"'}look-alike{'"'} audience.
            </div>
          </div>
          <div>
            <RadioGroup
              name={`${radioGroupNamePrefix}-isCustomAudience`}
              direction="horizontal"
              options={[
                { label: "Yes", value: "YES" },
                { label: "No", value: "NO" },
              ]}
              selectedValue={
                recipient.usesCustomAudience == null
                  ? undefined
                  : recipient.usesCustomAudience
                  ? "YES"
                  : "NO"
              }
              onChange={(newValue: string) =>
                saveAnswers({ usesCustomAudience: newValue === "YES" })
              }
            />
          </div>
        </div>
      )}

      {recipient.ccpaIsSharing &&
        recipient.usesCustomAudience == true &&
        vendor.category === VENDOR_CATEGORY_AD_NETWORK && (
          <ManagedByVendorSection
            radioGroupNamePrefix={radioGroupNamePrefix}
            org={org}
            vendor={vendor}
            allVendors={allVendors}
            portalAutocompleteDropdown={portalAutocompleteDropdown}
            onManagerAdded={onManagerAdded}
          />
        )}
    </div>
  );
}
